import React, { useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Avatar,
  Typography,
  Button,
  styled,
  keyframes,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import VideocamIcon from "@mui/icons-material/Videocam";
import { ChatUser } from "../types/chat";

interface ModalVideoIncomingProps {
  open: boolean;
  caller: ChatUser | null;
  onAccept: () => void;
  onReject: () => void;
}

// Animação de anel pulsante
const ringPulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "20px",
    padding: "40px 32px",
    minWidth: "420px",
    maxWidth: "500px",
    background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    color: "#fff",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
  },
}));

const AvatarWrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginBottom: "24px",
  position: "relative",
  "& .MuiAvatar-root": {
    width: "140px",
    height: "140px",
    border: "5px solid rgba(255, 255, 255, 0.4)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-10px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "160px",
    height: "160px",
    borderRadius: "50%",
    border: "3px solid rgba(255, 255, 255, 0.3)",
    animation: `${ringPulse} 2s ease-in-out infinite`,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: "-20px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "180px",
    height: "180px",
    borderRadius: "50%",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    animation: `${ringPulse} 2s ease-in-out infinite 0.5s`,
  },
});

const ButtonsContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "24px",
  marginTop: "40px",
});

const AcceptButton = styled(Button)({
  width: "140px",
  padding: "16px 24px",
  borderRadius: "50px",
  backgroundColor: "#4caf50",
  color: "#fff",
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
  "&:hover": {
    backgroundColor: "#45a049",
    boxShadow: "0 6px 16px rgba(76, 175, 80, 0.5)",
  },
});

const RejectButton = styled(Button)({
  width: "140px",
  padding: "16px 24px",
  borderRadius: "50px",
  backgroundColor: "#f44336",
  color: "#fff",
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(244, 67, 54, 0.4)",
  "&:hover": {
    backgroundColor: "#d32f2f",
    boxShadow: "0 6px 16px rgba(244, 67, 54, 0.5)",
  },
});

const VideoIcon = styled(VideocamIcon)({
  fontSize: "32px",
  color: "rgba(255, 255, 255, 0.9)",
  marginBottom: "12px",
});

const ModalVideoIncoming: React.FC<ModalVideoIncomingProps> = ({
  open,
  caller,
  onAccept,
  onReject,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Tocar som de chamada
  useEffect(() => {
    if (open) {
      // Criar elemento de áudio para toque
      const audio = new Audio();
      // Usar um tom de chamada - pode ser substituído por um arquivo de áudio real
      // Por enquanto, vamos usar um beep simples
      audio.src = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZWAIE";
      audio.loop = true;
      audio.volume = 0.4;
      audioRef.current = audio;

      // Tentar tocar
      audio.play().catch((error) => {
        console.warn("Não foi possível tocar o som de chamada:", error);
      });

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      };
    }
  }, [open]);

  const handleAccept = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    onAccept();
  };

  const handleReject = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    onReject();
  };

  const getCallerName = () => {
    if (!caller) return "Usuário";
    return caller.nomecomp || caller.nome || "Usuário";
  };

  const getCallerAvatar = () => {
    return caller?.img_usuario;
  };

  return (
    <StyledDialog open={open} maxWidth="sm" fullWidth>
      <Box textAlign="center">
        <VideoIcon />

        <Typography
          variant="body2"
          sx={{ opacity: 0.8, marginBottom: "20px", fontSize: "14px" }}
        >
          Chamada de vídeo recebida
        </Typography>

        <AvatarWrapper>
          <Avatar
            src={getCallerAvatar()}
            alt={getCallerName()}
            sx={{
              bgcolor: "#7b1fa2",
              fontSize: "48px",
              fontWeight: 600,
            }}
          >
            {getCallerName().charAt(0).toUpperCase()}
          </Avatar>
        </AvatarWrapper>

        <Typography variant="h4" fontWeight={700} gutterBottom>
          {getCallerName()}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            marginTop: "8px",
            fontSize: "16px",
          }}
        >
          está te chamando...
        </Typography>

        <ButtonsContainer>
          <RejectButton
            onClick={handleReject}
            startIcon={<PhoneDisabledIcon />}
          >
            Recusar
          </RejectButton>
          <AcceptButton
            onClick={handleAccept}
            startIcon={<PhoneIcon />}
          >
            Atender
          </AcceptButton>
        </ButtonsContainer>
      </Box>
    </StyledDialog>
  );
};

export default ModalVideoIncoming;
